import {customElement, property, query} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import type {PointTuple} from '#lit/lib/point.js';
import {Point} from '#lit/lib/point.js';
import {Cursor} from '#lit/lib/cursor.js';
import {ElementEventType} from '#lit/common/constants/index.js';
import {change} from '#lit/utils/commands/index.js';
import {listenerGenerator} from '#lit/utils/dom.js';
import type {CanvasElementEventDetail} from '../canvas_element/index.js';
import {CanvasElementEventAction} from '../canvas_element/index.js';
import type CanvasWindow from '../canvas_window/index.js';
import CanvasNode from '../canvas_node/index.js';
import CanvasGroup from '../canvas_group/index.js';
import type CanvasElement from '../canvas_element/index.js';
import styles from './style.scss';
import {calculateScale} from './utils.js';
import {isCanvasGroupType, isCanvasNodeType} from '#lit/types/canvas.js';
import FlyModal from '../modal/index.js';
import FlyInput from '../input/index.js';

export const CANVAS_ELEMENT_TAG = 'fly-canvas';

/**
 * A draggable, interactable surface used to build node-based UIs.
 */
@customElement(CANVAS_ELEMENT_TAG)
export default class FlyCanvas extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @property({type: Array}) initialPosition: PointTuple = [0, 0];

  @query('#canvas-plane') $plane?: HTMLElement;
  @query('#canvas-window') $window?: CanvasWindow;
  @query('#canvas-background') $background?: SVGElement;

  cursor = Cursor.for(this);

  get position(): Point {
    return Point.fromTuple(this.model.position.asTuple());
  }

  private attachListenersToElement(element: CanvasElement) {
    const getKey = (e: Event) => {
      const {detail} = e as CustomEvent<CanvasElementEventDetail>;
      return detail.element.key;
    };

    listenerGenerator<CustomEvent<CanvasElementEventDetail>>(element)
      .add(CanvasElementEventAction.Selected, e => {
        this.handleUpdate(Msg.addSelectedNode(getKey(e)));
      })
      .add(CanvasElementEventAction.Deselected, e =>
        this.handleUpdate(Msg.removeSelectedNode(getKey(e)))
      )
      .add(CanvasElementEventAction.Destroyed, e => {
        this.handleUpdate(Msg.removeSelectedNode(getKey(e)));
        this.handleUpdate(Msg.removeNode(getKey(e)));
      })
      .add(CanvasElementEventAction.Moved, () => change(this))
      .add(ElementEventType.Change, () => change(this));
  }

  protected updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    this.dispatchEvent(
      new CustomEvent('canvas-updated', {
        detail: {
          element: this,
          changedProperties,
        },
        bubbles: false,
        cancelable: true,
        composed: false,
      })
    );
  }

  registerNode(node: CanvasNode) {
    if (!node.model.attached) node.attach(this);

    if (isCanvasNodeType(node)) {
      this.attachListenersToElement(node);
      this.handleUpdate(Msg.registerNode(node));
    }
  }

  removeNode(nodeId: string) {
    this.handleUpdate(Msg.removeNode(nodeId));
  }

  registerGroup(group: CanvasGroup) {
    if (!isCanvasGroupType(group)) return;
    if (!group.model.attached) group.attach(this);

    this.attachListenersToElement(group);
    this.handleUpdate(Msg.registerGroup(group));
  }

  deselectAll() {
    this.handleUpdate(Msg.deselectAll());
  }

  isInPath(target: Element | undefined, path: EventTarget[]): boolean {
    return !!(
      this.$plane &&
      this.$background &&
      target instanceof Element &&
      [this, this.$plane, this.$background].some(el => {
        return !!(
          target?.isSameNode(el) ||
          this.$background?.contains(target!) ||
          (!CanvasNode.isInPath(path ?? []) &&
            !CanvasGroup.isInPath(path ?? []))
        );
      })
    );
  }

  cursorOnPlane(predicate?: (el: Element) => boolean) {
    const hasElement = predicate
      ? this.cursor.composedPath.find(el => {
          return el instanceof Element ? predicate!(el) : false;
        })
      : true;

    return (
      this.cursor.composedPath.find(el => el === this) &&
      !this.cursor.composedPath.find(el => el instanceof FlyModal) &&
      !this.cursor.composedPath.find(el => el instanceof FlyInput) &&
      !this.cursor.composedPath.find(el => el instanceof HTMLInputElement) &&
      hasElement
    );
  }

  updateScale(delta: number) {
    this.handleUpdate(Msg.setScale(calculateScale(this.model.scale, delta)));
  }
  setScale(scaleValue: number) {
    this.handleUpdate(Msg.setScale(calculateScale(scaleValue, 0)));
  }

  static get styles() {
    return [styles];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CANVAS_ELEMENT_TAG]: FlyCanvas;
  }

  interface HTMLElementEventMap {
    'canvas-updated': CustomEvent<{
      element: FlywheelComponent;
      changedProperties: Map<PropertyKey, unknown>;
    }>;
  }
}
